import { FUNCTION_CATALOG_NAMES } from "./function-catalog.js";
// The function gate is DERIVED from the catalog — never hand-maintained — so a
// name added to FORMULA_FUNCTION_CATALOG is automatically recognized here and a
// name removed there stops tokenizing as a function. See function-catalog.ts.
const FUNCTIONS = FUNCTION_CATALOG_NAMES;
const TWO_CHAR_OPS = new Set(["==", "!=", ">=", "<=", "&&", "||"]);
const SINGLE_OPS = new Set(["+", "-", "*", "/", "%", ">", "<", "!"]);
export function tokenize(expression) {
    const tokens = [];
    let i = 0;
    const len = expression.length;
    while (i < len) {
        // Skip whitespace
        if (expression[i] === " " || expression[i] === "\t" || expression[i] === "\n") {
            i++;
            continue;
        }
        const pos = i;
        const ch = expression[i];
        // String literal
        if (ch === '"' || ch === "'") {
            const quote = ch;
            i++;
            let val = "";
            while (i < len && expression[i] !== quote) {
                if (expression[i] === "\\") {
                    i++;
                    if (i < len)
                        val += expression[i];
                }
                else {
                    val += expression[i];
                }
                i++;
            }
            if (i >= len)
                throw new ParseError(`Unterminated string at position ${pos}`, pos);
            i++; // skip closing quote
            tokens.push({ type: "string", value: val, position: pos });
            continue;
        }
        // Field reference: {fieldName} or {group.field}
        if (ch === "{") {
            i++;
            let name = "";
            while (i < len && expression[i] !== "}") {
                name += expression[i];
                i++;
            }
            if (i >= len)
                throw new ParseError(`Unclosed field reference at position ${pos}`, pos);
            i++; // skip }
            tokens.push({ type: "fieldRef", value: name, position: pos });
            continue;
        }
        // Number (including negative after operator or start)
        if (isDigit(ch) || (ch === "-" && isNegativeNumberStart(tokens, expression, i))) {
            let num = "";
            if (ch === "-") {
                num = "-";
                i++;
            }
            while (i < len && (isDigit(expression[i]) || expression[i] === ".")) {
                num += expression[i];
                i++;
            }
            tokens.push({ type: "number", value: num, position: pos });
            continue;
        }
        // Parentheses
        if (ch === "(") {
            tokens.push({ type: "lparen", value: "(", position: pos });
            i++;
            continue;
        }
        if (ch === ")") {
            tokens.push({ type: "rparen", value: ")", position: pos });
            i++;
            continue;
        }
        // Comma
        if (ch === ",") {
            tokens.push({ type: "comma", value: ",", position: pos });
            i++;
            continue;
        }
        // Two-character operators
        if (i + 1 < len) {
            const two = expression[i] + expression[i + 1];
            if (TWO_CHAR_OPS.has(two)) {
                tokens.push({ type: "operator", value: two, position: pos });
                i += 2;
                continue;
            }
        }
        // Single-character operators
        if (SINGLE_OPS.has(ch)) {
            tokens.push({ type: "operator", value: ch, position: pos });
            i++;
            continue;
        }
        // Identifier (function name) or field ref in dotted notation without braces (for line items inside function args)
        if (isAlpha(ch) || ch === "_") {
            let ident = "";
            while (i < len && (isAlphaNum(expression[i]) || expression[i] === "_")) {
                ident += expression[i];
                i++;
            }
            // Check for dotted notation (e.g., lineItems.amount, or multi-segment
            // refs like coupon.field.invoice_total used by TXN_* aggregates —
            // SP5 rows may themselves carry a dotted key such as 'field.invoice_total').
            if (i < len && expression[i] === ".") {
                while (i < len && expression[i] === ".") {
                    ident += ".";
                    i++;
                    while (i < len && (isAlphaNum(expression[i]) || expression[i] === "_")) {
                        ident += expression[i];
                        i++;
                    }
                }
                // Dotted ident that's not a function → fieldRef
                tokens.push({ type: "fieldRef", value: ident, position: pos });
                continue;
            }
            if (FUNCTIONS.has(ident)) {
                tokens.push({ type: "function", value: ident, position: pos });
            }
            else if (ident === "true") {
                tokens.push({ type: "number", value: "true", position: pos });
            }
            else if (ident === "false") {
                tokens.push({ type: "number", value: "false", position: pos });
            }
            else if (ident === "null") {
                // Literal null keyword — beside true/false. Behavior-identical to the
                // pre-P4 accident where bare `null` fell through to an unresolvable
                // fieldRef (which resolved to null); this makes it an intentional token.
                tokens.push({ type: "number", value: "null", position: pos });
            }
            else {
                // Treat as bare identifier → field ref (support unbraced field names in some contexts)
                tokens.push({ type: "fieldRef", value: ident, position: pos });
            }
            continue;
        }
        throw new ParseError(`Unexpected character '${ch}' at position ${pos}`, pos);
    }
    tokens.push({ type: "eof", value: "", position: i });
    return tokens;
}
function isDigit(ch) {
    return ch >= "0" && ch <= "9";
}
function isAlpha(ch) {
    return (ch >= "a" && ch <= "z") || (ch >= "A" && ch <= "Z");
}
function isAlphaNum(ch) {
    return isAlpha(ch) || isDigit(ch);
}
/**
 * Check if a `-` should be treated as a negative number sign rather than subtraction.
 * It's negative if there's no preceding value token (start of expression, after operator, after lparen/comma).
 */
function isNegativeNumberStart(tokens, expression, pos) {
    // Must have a digit following the -
    if (pos + 1 >= expression.length || !isDigit(expression[pos + 1]))
        return false;
    if (tokens.length === 0)
        return true;
    const prev = tokens[tokens.length - 1];
    return prev.type === "operator" || prev.type === "lparen" || prev.type === "comma";
}
export class ParseError extends Error {
    position;
    constructor(message, position) {
        super(message);
        this.name = "ParseError";
        this.position = position;
    }
}
//# sourceMappingURL=tokenizer.js.map